import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LinkedinURL from './slides/linkedInUrl';
import CompanyForm from './slides/companyForm';
import UserRole from './slides/userRole';
import ConnectSlack from './slides/connectSlack';
import { useSlackIntegration } from '../../hooks/useSlackIntegration';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Organization } from '@/types/models';
import { useOrganization } from '../../hooks/useOrganization';
import axios from 'axios';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import { signOut } from '../../utils/auth';

const slideVariants = {
  enter: { y: 100, opacity: 0 },
  center: { y: 0, opacity: 1 },
  exit: { y: -100, opacity: 0 }
};

const Onboard: React.FC = () => {
  const { state, updateState } = useOnboardingState();
  const { handleOAuthCallback, status } = useSlackIntegration();
  const navigate = useNavigate();
  const router = useRouter();
  const { organization, isLoading, error } = useOrganization();

  // Get current step from URL
  const getCurrentStep = () => {
    const path = router.state.location.pathname;
    switch (path) {
      case '/linkedinconnect':
        return 0;
      case '/companydetails':
        return 1;
      case '/userdetails':
        return 2;
      case '/slackconnect':
        return 3;
      default:
        return 0;
    }
  };

  const navigateToStep = (step: number) => {
    const paths = [
      '/linkedinconnect',
      '/companydetails',
      '/userdetails',
      '/slackconnect'
    ];
    
    // Update state first
    updateState({
      ...state,
      currentStep: step
    });

    // Navigate immediately without setTimeout
    navigate({ 
      to: paths[step],
      replace: true 
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    
    if (code || error) {
      // Only handle OAuth if we're on the slack connect page
      const currentPath = router.state.location.pathname;
      if (currentPath === '/slackconnect') {
        if (code) {
          handleOAuthCallback.mutate(code, {
            onSuccess: () => {
              // Stay on the same page after successful connection
              navigateToStep(3);
            },
            onError: (error) => {
              console.error('Failed to connect Slack:', error);
            }
          });
        } else if (error) {
          console.error('Slack OAuth error:', error);
        }
      }
    }
  }, []);

  useEffect(() => {
    // Comment out the 401 error redirect
    // if (axios.isAxiosError(error) && error.response?.status === 401) {
    //   navigate({ to: '/signin' });
    // }
  }, [error, navigate]);

  // Update state based on current URL without forcing navigation
  useEffect(() => {
    const currentStepFromUrl = getCurrentStep();
    updateState({
      ...state,
      currentStep: currentStepFromUrl
    });
  }, [router.state.location.pathname]);

  const handleLinkedInNext = async (updatedOrg?: Organization) => {
    if (!updatedOrg) {
      return; // Don't proceed if there's no organization data
    }

    if (updatedOrg?.linkedin_profile_url) {
      updateState({
        linkedinUrl: updatedOrg.linkedin_profile_url,
        companyDetails: {
          ...state.companyDetails,
          companyName: updatedOrg.linkedin_company_profile?.name || '',
          companySize: updatedOrg.linkedin_company_profile?.size || '',
          industry: updatedOrg.linkedin_company_profile?.industry || '',
          website: updatedOrg.linkedin_company_profile?.company_website || '',
        }
      });
      navigateToStep(1);
    }
  };

  const currentStep = getCurrentStep();

  const slides = [
    <LinkedinURL 
      key="linkedin"
      onNext={handleLinkedInNext} 
      onBack={() => {
        signOut();
        navigate({ to: '/signin' });
      }}
      currentStep={currentStep} 
      totalSteps={4} 
      initialUrl={state.linkedinUrl}
    />,
    <CompanyForm 
      key="company"
      onNext={() => navigateToStep(2)} 
      onBack={() => navigateToStep(0)}
      currentStep={currentStep} 
      totalSteps={4}
      initialData={state.companyDetails}
    />,
    <UserRole
      currentStep={currentStep}
      totalSteps={4}
      onNext={() => navigateToStep(3)}
      onBack={() => navigateToStep(1)}
      initialData={state.userDetails}
    />,
    <ConnectSlack
      currentStep={currentStep}
      totalSteps={4}
      onNext={() => navigate({ 
        to: '/intent-signals/',
        search: { startTour: 'true' }
      })}
      onBack={() => navigateToStep(2)}
    />
  ];

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={currentStep}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="min-h-screen w-full"
      >
        {slides[currentStep]}
      </motion.div>
    </AnimatePresence>
  );
};

export default Onboard;
